import { NewsletterTrackingSettingsPage } from '../../newsletter/src/NewsletterEngagementPanel';
import { NewsletterEmailTemplatesPage } from '../../newsletter/src/NewsletterEmailTemplatesPage';
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  IconAddressBook,
  IconCalendarEvent,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCreditCard,
  IconLayoutGrid,
  IconLoader2,
  IconLogout2,
  IconMail,
  IconMenu2,
  IconPhoto,
  IconBrandInstagram,
  IconSettings,
  IconX,
} from "@tabler/icons-react";
import {
  Navigate,
  NavLink,
  Link,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  isRouteErrorResponse,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router-dom";

import { AppProviders, dispatchEasyShellContext, fetchJson } from "@easy-core";

import "@easy-ui/legacy/workspaceBase.css";
import { createHostedContactsRoutes } from "../../contacts/src/App";
import { createHostedEventPlannerRoutes, HostedEventSettingsPage } from "../../event-planner/src/App";
import { createHostedMediaRoutes } from "../../media/src/App";
import { createHostedNewsletterRoutes, HostedNewsletterSettingsPage } from "../../newsletter/src/App";
import { createHostedSocialPublicationRoutes } from "../../social-publications/src/App";
import { getCapabilities } from "../../social-publications/src/socialClient";
import { HostedSessionProvider, useHostedSession, type HostedSubscription } from "./hostedSession";
import "./workspaceHosted.css";
import "@easy-ui/hostedTheme.css";
import { PageHeader } from "@easy-ui";
import { PlatformTenantsPage } from "./accounts/PlatformTenantsPage";
import { PlatformTenantPage } from "./accounts/PlatformTenantPage";
import { TenantUsersPage } from "./accounts/TenantUsersPage";
import { isManager, roleLabel } from "./accounts/types";
import { InvitationActivationPage } from "./accounts/InvitationActivationPage";

const SIDEBAR_COLLAPSE_STORAGE_KEY = "easy-hosted-workspace:sidebar-collapsed";
const RESPONSIVE_NAV_MEDIA_QUERY = "(max-width: 1120px)";
const MOBILE_NAV_MEDIA_QUERY = "(max-width: 840px)";

type HealthPayload = {
  status: string;
  env: string;
  app_base_url: string;
  object_storage_configured: boolean;
};

type BillingPayload = HostedSubscription;


type HostedNavEntry = {
  app_id: string;
  to: string;
  label: string;
  description: string;
  icon: typeof IconLayoutGrid;
};

const PRIMARY_NAV: HostedNavEntry[] = [
  {
    app_id: "event-planner",
    to: "/events",
    label: "Évènements",
    description: "Catalogue, détail et publication",
    icon: IconCalendarEvent,
  },
  {
    app_id: "contacts",
    to: "/contacts",
    label: "Contacts",
    description: "Audience, groupes et import",
    icon: IconAddressBook,
  },
  {
    app_id: "media",
    to: "/media",
    label: "Médias",
    description: "Bibliothèque partagée",
    icon: IconPhoto,
  },
  {
    app_id: "newsletter",
    to: "/newsletter",
    label: "Newsletter",
    description: "Brouillons et campagnes envoyées",
    icon: IconMail,
  },
];

const SOCIAL_NAV: HostedNavEntry = { app_id: 'social', to: '/social', label: 'Réseaux sociaux', description: 'Publications Facebook et Instagram', icon: IconBrandInstagram };

const ACCOUNT_NAV: HostedNavEntry[] = [
  { app_id: "admin", to: "/admin/companies", label: "Administration Relyo", description: "Entreprises et accès", icon: IconLayoutGrid },
  {
    app_id: "settings",
    to: "/settings",
    label: "Réglages",
    description: "Compte, emails et connexions",
    icon: IconSettings,
  },
  {
    app_id: "billing",
    to: "/billing",
    label: "Abonnement",
    description: "Plan, essai et quotas",
    icon: IconCreditCard,
  },
];

const HOSTED_NAV_ITEMS = [...PRIMARY_NAV, SOCIAL_NAV, ...ACCOUNT_NAV];

function RelioWordmark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 226.56 139.3"
      role="img"
      aria-label="Relyo"
    >
      <g>
        <path d="M29.79,70.68c-.23,3.79,2.82,7.37,6.8,8.09a11.69,11.69,0,0,0,9.76-2.63c.3-.24.57-.51.87-.73a1.1,1.1,0,0,1,1.72.14c.9.91,1.78,1.83,2.66,2.74.63.66.67,1.08,0,1.7a18.57,18.57,0,0,1-15.18,5.29,16.44,16.44,0,0,1-7.57-2.66,13.79,13.79,0,0,1-5.81-7.57c-1.67-5.49-1.17-10.76,2.48-15.48a14.47,14.47,0,0,1,9.66-5.41,17.68,17.68,0,0,1,10,1.17,13.92,13.92,0,0,1,7,6.58,16.56,16.56,0,0,1,1.69,7c0,1.42-.27,1.73-1.77,1.73H29.79Zm15.87-5.19c-.33-3.09-3.45-5.48-7.17-5.56-3.9-.08-7.63,2.45-8.11,5.56Z" />
        <path d="M6.68,58.65A31.73,31.73,0,0,1,9.1,56.18a10,10,0,0,1,9.13-1.89,1,1,0,0,1,.89,1.42c-.36,1.4-.73,2.81-1.12,4.2-.25.86-.64,1.06-1.63.88a8.1,8.1,0,0,0-5.36.47,5.68,5.68,0,0,0-2.94,4.19A19.86,19.86,0,0,0,7.7,69.3c0,4.66,0,9.32,0,14,0,1.21-.27,1.47-1.57,1.48-1.56,0-3.12,0-4.68,0-1.06,0-1.4-.34-1.4-1.33L0,56c0-1.21.24-1.43,1.51-1.44,1,0,2,0,2.93,0a1.28,1.28,0,0,1,1.42,1.06C6.11,56.61,6.39,57.59,6.68,58.65Z" />
        <path d="M209.37,52.64A17.81,17.81,0,0,1,221.62,57a14.08,14.08,0,0,1,4.62,8.21,15.42,15.42,0,0,1-1.51,10.53c-2.64,4.73-6.92,7.35-12.46,8.15a19.11,19.11,0,0,1-9.52-.9c-5.81-2.16-9.27-6.18-10.29-12A15.33,15.33,0,0,1,194,61a14.75,14.75,0,0,1,6.1-6.12A18.53,18.53,0,0,1,209.37,52.64Zm9.52,15.63c0-.46-.09-1.12-.21-1.77-1.28-7.18-9.71-9.24-14.84-5.76-3.37,2.3-4.38,5.61-3.75,9.36,1.18,7.12,9.08,9.17,14.34,6.21C217.52,74.56,218.76,71.77,218.89,68.27Z" />
        <path d="M186.51,68.33V81.84c0,1.34-.3,1.63-1.7,1.63s-3.06,0-4.59,0c-1.05,0-1.41-.35-1.45-1.33,0-.66,0-1.32,0-2V55.51c0-.23,0-.47,0-.71.05-1.25.34-1.52,1.64-1.53,1.51,0,3,0,4.51,0,1.31,0,1.6.28,1.6,1.55Z" />
        <path d="M182.62,41.28a4.53,4.53,0,0,1,4.71,4.47,4.73,4.73,0,0,1-9.44,0A4.5,4.5,0,0,1,182.62,41.28Z" />
        <path d="M168.72,77.12h-.14a99.17,99.17,0,0,0-17.1,3.14,39.24,39.24,0,0,0-1.78-6.45c-2.26-6-5.61-9.65-9.21-10-4.08-.47-6.54,3.34-8.7,6.7-1.53,2.36-3.6,5.62-5.28,5.51-2.27-.1-5.12-4.81-6.82-7.63-2.19-3.61-4.25-7-7.8-6-2.69.77-3.7,3.49-4.87,6.64-1,2.68-2.24,6-4.37,7.9a5,5,0,0,1-3.68.85c-3.87-.31-8.32-2.88-11.34-6.56a30.8,30.8,0,0,1-4.15-6.85,66.85,66.85,0,0,0,7.33-4.66C105.18,49.11,114.29,33.16,115.19,17c.21-3.88.86-15.67-7.23-16.88-6.62-1-13.37,5.05-17.87,9.68C76.4,23.87,70.53,42.13,74.4,58.63c.18.78.38,1.55.61,2.32-.76.34-1.53.67-2.32,1-1.31.54-9.67,4.35-13.5,6.1a.67.67,0,0,0-.33.9l2.27,5a.67.67,0,0,0,.9.34c5.89-2.69,12.31-5.6,13.26-6,.71-.29,1.4-.59,2.09-.89a37.44,37.44,0,0,0,5,8.16c4.2,5.12,10.36,8.58,16.07,9A11.44,11.44,0,0,0,107.16,82c3.41-3,5-7.42,6.24-10.62l.06-.16.4.66c2.72,4.49,6.44,10.63,12.34,10.91,5.57.27,8.89-4.88,11.31-8.63A25.92,25.92,0,0,1,140,70.72c.94.72,2.92,3.27,4.27,8.43.19.75.43,1.81.64,3.12-11.81,4-25.58,11-35.51,23.32C102,114.77,97.45,128,99.53,134.52a6.26,6.26,0,0,0,4.25,4.36,12.77,12.77,0,0,0,3.4.42c8.94,0,22.08-7.49,32.36-18.84A47.27,47.27,0,0,0,152.2,87.12,104.35,104.35,0,0,1,164,84.51c2.06-.33,3.57-.5,4.32-.57l.28,0a.4.4,0,0,0,.26-.3,0,0,0,0,0,0,0v-.11s0,0,0,0l.19-5.81v-.17A.42.42,0,0,0,168.72,77.12ZM81.21,57.81,81,57.07C77.7,42.85,82.91,26.94,95,14.54c6.09-6.26,9.57-7.73,11.49-7.73a3.6,3.6,0,0,1,.49,0s2,.4,1.43,9.8c-.78,14.13-8.86,28.16-21.6,37.54A62,62,0,0,1,81.21,57.81Zm53.29,58.07C123.26,128.28,111,133.34,106,132.4c-1-2.59,1.52-13.65,8.7-22.52,8.47-10.48,20.26-16.79,30.68-20.56A40.16,40.16,0,0,1,134.5,115.88Z" />
      </g>
    </svg>
  );
}

function RelioMonogram({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="58 0 112 140"
      role="img"
      aria-label="Relyo compact mark"
    >
      <path d="M168.72,77.12h-.14a99.17,99.17,0,0,0-17.1,3.14,39.24,39.24,0,0,0-1.78-6.45c-2.26-6-5.61-9.65-9.21-10-4.08-.47-6.54,3.34-8.7,6.7-1.53,2.36-3.6,5.62-5.28,5.51-2.27-.1-5.12-4.81-6.82-7.63-2.19-3.61-4.25-7-7.8-6-2.69.77-3.7,3.49-4.87,6.64-1,2.68-2.24,6-4.37,7.9a5,5,0,0,1-3.68.85c-3.87-.31-8.32-2.88-11.34-6.56a30.8,30.8,0,0,1-4.15-6.85,66.85,66.85,0,0,0,7.33-4.66C105.18,49.11,114.29,33.16,115.19,17c.21-3.88.86-15.67-7.23-16.88-6.62-1-13.37,5.05-17.87,9.68C76.4,23.87,70.53,42.13,74.4,58.63c.18.78.38,1.55.61,2.32-.76.34-1.53.67-2.32,1-1.31.54-9.67,4.35-13.5,6.1a.67.67,0,0,0-.33.9l2.27,5a.67.67,0,0,0,.9.34c5.89-2.69,12.31-5.6,13.26-6,.71-.29,1.4-.59,2.09-.89a37.44,37.44,0,0,0,5,8.16c4.2,5.12,10.36,8.58,16.07,9A11.44,11.44,0,0,0,107.16,82c3.41-3,5-7.42,6.24-10.62l.06-.16.4.66c2.72,4.49,6.44,10.63,12.34,10.91,5.57.27,8.89-4.88,11.31-8.63A25.92,25.92,0,0,1,140,70.72c.94.72,2.92,3.27,4.27,8.43.19.75.43,1.81.64,3.12-11.81,4-25.58,11-35.51,23.32C102,114.77,97.45,128,99.53,134.52a6.26,6.26,0,0,0,4.25,4.36,12.77,12.77,0,0,0,3.4.42c8.94,0,22.08-7.49,32.36-18.84A47.27,47.27,0,0,0,152.2,87.12,104.35,104.35,0,0,1,164,84.51c2.06-.33,3.57-.5,4.32-.57l.28,0a.4.4,0,0,0,.26-.3,0,0,0,0,0,0,0v-.11s0,0,0,0l.19-5.81v-.17A.42.42,0,0,0,168.72,77.12ZM81.21,57.81,81,57.07C77.7,42.85,82.91,26.94,95,14.54c6.09-6.26,9.57-7.73,11.49-7.73a3.6,3.6,0,0,1,.49,0s2,.4,1.43,9.8c-.78,14.13-8.86,28.16-21.6,37.54A62,62,0,0,1,81.21,57.81Zm53.29,58.07C123.26,128.28,111,133.34,106,132.4c-1-2.59,1.52-13.65,8.7-22.52,8.47-10.48,20.26-16.79,30.68-20.56A40.16,40.16,0,0,1,134.5,115.88Z" />
    </svg>
  );
}

function readStoredCollapseState() {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeStoredCollapseState(nextValue: boolean) {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, String(nextValue));
  } catch {
    // Ignore localStorage failures in local smoke mode.
  }
}

function formatIsoDate(value: string | null) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function useMediaQueryState(query: string) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQueryList = window.matchMedia(query);
    const updateMatches = (event?: MediaQueryListEvent) => {
      setMatches(event?.matches ?? mediaQueryList.matches);
    };

    updateMatches();

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", updateMatches);
      return () => mediaQueryList.removeEventListener("change", updateMatches);
    }

    mediaQueryList.addListener(updateMatches);
    return () => mediaQueryList.removeListener(updateMatches);
  }, [query]);

  return matches;
}

function AppNavIcon({ appId }: { appId: string }) {
  const matchingEntry = HOSTED_NAV_ITEMS.find((entry) => entry.app_id === appId);
  const Icon = matchingEntry?.icon || IconLayoutGrid;
  return <Icon className="nav-icon" size="1em" stroke={1.9} aria-hidden={true} />;
}

function AuthCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isSubmitting, session, state } = useHostedSession();
  const redirectTarget = useMemo(() => {
    const rawValue = new URLSearchParams(location.search).get("redirect");
    if (!rawValue || !rawValue.startsWith("/") || rawValue.startsWith("//")) {
      return "/events";
    }
    return rawValue;
  }, [location.search]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage("");
    try {
      await login({ email, password });
      navigate(redirectTarget, { replace: true });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Impossible d'ouvrir la session.");
    }
  }

  if (state === "authenticated" && session) {
    return <Navigate to={redirectTarget} replace />;
  }

  return (
    <div className="hosted-auth-shell">
      <section className="hosted-auth-card">
        <div className="hosted-auth-brand">
          <RelioWordmark className="hosted-auth-wordmark" />
          <div className="hosted-auth-brand-copy">
            <p className="hosted-kicker">Relyo</p>
            <h1>Ouvrir la session</h1>
            <p className="hosted-note">
              Votre accès est fourni par le responsable de votre entreprise ou par Relyo.
            </p>
          </div>
        </div>

        <form className="hosted-auth-form" onSubmit={handleSubmit}>
          <label>
            Email
            <input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="bonjour@relyo.app" />
          </label>

          <label>
            Mot de passe
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Au moins 8 caractères"
            />
          </label>

          {errorMessage ? <p className="hosted-inline-status is-error">{errorMessage}</p> : null}

          <div className="hosted-auth-actions">
            <button type="submit" className="hosted-primary-button" disabled={isSubmitting}>
              {isSubmitting ? "Chargement..." : "Entrer"}
            </button>

          </div>
        </form>
      </section>
    </div>
  );
}

function RequireHostedSession() {
  const location = useLocation();
  const { state, errorMessage } = useHostedSession();

  if (state === "loading") {
    return (
      <div className="hosted-loading-state">
        <IconLoader2 className="hosted-spinner" size="1.2em" stroke={1.9} />
        <span>Chargement de la session...</span>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="hosted-loading-state is-error">
        <strong>Impossible de charger la session.</strong>
        <span>{errorMessage}</span>
      </div>
    );
  }

  if (state !== "authenticated") {
    const redirectTarget = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectTarget)}`} replace />;
  }

  return <Outlet />;
}

function HostedPlaceholderPage({
  title,
  description,
  kicker = "Bientôt",
}: {
  title: string;
  description: string;
  kicker?: string;
}) {
  return (
    <section className="hosted-content-card">
      <p className="hosted-kicker">{kicker}</p>
      <h2>{title}</h2>
      <p className="hosted-note">{description}</p>
    </section>
  );
}

function describeHostedRouteError(error: unknown) {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        title: "Page introuvable",
        message: "La page demandée n'existe pas ou n'est plus disponible dans cet espace.",
        technicalDetails: `${error.status} ${error.statusText}`.trim(),
      };
    }

    if (error.status === 401 || error.status === 403) {
      return {
        title: "Accès indisponible",
        message: "Votre session ne permet pas d'afficher cette page pour le moment.",
        technicalDetails: `${error.status} ${error.statusText}`.trim(),
      };
    }

    return {
      title: "Une erreur est survenue",
      message: "La page n'a pas pu s'afficher correctement. Vous pouvez recharger l'interface et reprendre depuis le menu.",
      technicalDetails: `${error.status} ${error.statusText}`.trim(),
    };
  }

  if (error instanceof Error) {
    return {
      title: "Une erreur est survenue",
      message: "La page n'a pas pu s'afficher correctement. Vous pouvez recharger l'interface et reprendre depuis le menu.",
      technicalDetails: error.message,
    };
  }

  return {
    title: "Une erreur est survenue",
    message: "La page n'a pas pu s'afficher correctement. Vous pouvez recharger l'interface et reprendre depuis le menu.",
    technicalDetails: null,
  };
}

function HostedRouteErrorPage() {
  const error = useRouteError();
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthRoute = location.pathname.startsWith("/login") || location.pathname.startsWith("/register") || location.pathname.startsWith("/activate");
  const { title, message, technicalDetails } = describeHostedRouteError(error);
  const canGoBack = typeof window !== "undefined" && window.history.length > 1;
  const showTechnicalDetails = import.meta.env.DEV && technicalDetails;

  useEffect(() => {
    console.error("Hosted route error", error);
  }, [error]);

  return (
    <div className="hosted-error-shell">
      <section className="hosted-error-card" role="alert" aria-live="assertive">
        <p className="hosted-kicker">Relyo</p>
        <h1>{title}</h1>
        <p className="hosted-error-message">{message}</p>

        <div className="hosted-error-actions">
          <button type="button" className="hosted-primary-button" onClick={() => window.location.reload()}>
            Recharger la page
          </button>
          <button
            type="button"
            className="hosted-secondary-button"
            onClick={() => navigate(isAuthRoute ? "/login" : "/events", { replace: true })}
          >
            {isAuthRoute ? "Retour à la connexion" : "Retour aux évènements"}
          </button>
          {canGoBack ? (
            <button type="button" className="hosted-secondary-button" onClick={() => navigate(-1)}>
              Revenir en arrière
            </button>
          ) : null}
        </div>

        <p className="hosted-note">
          Si le probleme revient, rechargez la page puis reconnectez-vous a l'espace avant de reprendre votre saisie.
        </p>

        {showTechnicalDetails ? (
          <details className="hosted-error-details">
            <summary>Details techniques</summary>
            <pre>{technicalDetails}</pre>
          </details>
        ) : null}
      </section>
    </div>
  );
}

function HostedSettingsLayout() {
  const location = useLocation();
  const { session } = useHostedSession();
  return (
    <div className="hosted-settings-layout">
      <PageHeader title="Réglages" />
      <nav className="hosted-settings-nav" aria-label="Rubriques des réglages">
        <NavLink to="/settings" end>Compte et espace</NavLink>
        {isManager(session?.membership.role) ? <NavLink to="/settings/users">Utilisateurs</NavLink> : null}
        <NavLink to="/settings/newsletter" end>Envoi des emails</NavLink>
        <Link to="/settings/newsletter/tracking" aria-current={location.pathname === "/settings/newsletter/tracking" || location.pathname === "/settings/newsletter/subscription-emails" ? "page" : undefined} className={location.pathname === "/settings/newsletter/tracking" || location.pathname === "/settings/newsletter/subscription-emails" ? "active" : undefined}>Suivi des ouvertures</Link>
        <NavLink to="/settings/events">Événements et connexions</NavLink>
      </nav>
      <div className="hosted-settings-content"><Outlet /></div>
    </div>
  );
}

function LegacySettingsRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

function HostedSettingsPage() {
  const { session } = useHostedSession();
  const healthQuery = useQuery({
    queryKey: ["hosted-health"],
    queryFn: () => fetchJson<HealthPayload>("/api/health"),
    retry: false,
  });

  return (
    <div className="hosted-page-grid">
      <section className="hosted-content-card">
        <p className="hosted-kicker">Espace</p>
        <h2>Informations du compte</h2>
        <div className="hosted-key-value-grid">
          <div>
            <strong>Espace</strong>
            <span>{session?.tenant.name || "-"}</span>
          </div>
          <div>
            <strong>Slug</strong>
            <span>{session?.tenant.slug || "-"}</span>
          </div>
          <div>
            <strong>Utilisateur</strong>
            <span>{session?.user.email || "-"}</span>
          </div>
          <div>
            <strong>Role</strong>
            <span>{roleLabel(session?.membership.role || "-")}</span>
          </div>
        </div>
      </section>

      <section className="hosted-content-card">
        <p className="hosted-kicker">Plateforme</p>
        <h2>État de la plateforme</h2>
        {healthQuery.isLoading ? <p className="hosted-inline-status">Chargement de l'état de la plateforme...</p> : null}
        {healthQuery.isError ? (
          <p className="hosted-inline-status is-error">
            {healthQuery.error instanceof Error ? healthQuery.error.message : "Impossible de lire l'état de la plateforme."}
          </p>
        ) : null}
        {healthQuery.data ? (
          <div className="hosted-key-value-grid">
            <div>
              <strong>Statut</strong>
              <span>{healthQuery.data.status}</span>
            </div>
            <div>
              <strong>Environnement</strong>
              <span>{healthQuery.data.env}</span>
            </div>
            <div>
              <strong>Base URL</strong>
              <span>{healthQuery.data.app_base_url || "-"}</span>
            </div>
            <div>
              <strong>Object storage</strong>
              <span>{healthQuery.data.object_storage_configured ? "Configuré" : "Non configuré"}</span>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function HostedBillingPage() {
  const { session } = useHostedSession();
  const billingQuery = useQuery({
    queryKey: ["hosted-billing"],
    queryFn: () => fetchJson<BillingPayload>("/api/billing/subscription"),
    initialData: session?.subscription || undefined,
    retry: false,
  });

  const subscription = billingQuery.data;
  const entitlements = Object.entries(subscription?.entitlements || {});

  return (
    <div className="hosted-page-grid">
      <section className="hosted-content-card">
        <p className="hosted-kicker">Abonnement</p>
        <h2>Abonnement de l'espace</h2>
        {billingQuery.isLoading ? <p className="hosted-inline-status">Chargement de l'abonnement...</p> : null}
        {billingQuery.isError ? (
          <p className="hosted-inline-status is-error">
            {billingQuery.error instanceof Error ? billingQuery.error.message : "Impossible de lire l'abonnement."}
          </p>
        ) : null}
        {subscription ? (
          <div className="hosted-key-value-grid">
            <div>
              <strong>Plan</strong>
              <span>{subscription.plan_code}</span>
            </div>
            <div>
              <strong>Statut</strong>
              <span>{subscription.status}</span>
            </div>
            <div>
              <strong>Provider</strong>
              <span>{subscription.provider}</span>
            </div>
            <div>
              <strong>Fin de trial</strong>
              <span>{formatIsoDate(subscription.trial_ends_at)}</span>
            </div>
            <div>
              <strong>Fin de periode</strong>
              <span>{formatIsoDate(subscription.current_period_end)}</span>
            </div>
          </div>
        ) : (
          <p className="hosted-note">Aucun abonnement n'est encore retourné par l'API.</p>
        )}
      </section>

      <section className="hosted-content-card">
        <p className="hosted-kicker">Quotas</p>
        <h2>Limites actuellement exposées</h2>
        {entitlements.length > 0 ? (
          <div className="hosted-key-value-grid">
            {entitlements.map(([key, value]) => (
              <div key={key}>
                <strong>{key}</strong>
                <span>{value}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="hosted-note">Les quotas de l'espace n'ont pas encore été renseignés.</p>
        )}
      </section>
    </div>
  );
}

function HostedShellLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, logout, isSubmitting } = useHostedSession();
  const socialCapabilities = useQuery({ queryKey: ['social-capabilities'], queryFn: getCapabilities, retry: false });
  const primaryNav = socialCapabilities.data?.enabled ? [...PRIMARY_NAV, SOCIAL_NAV] : PRIMARY_NAV;
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readStoredCollapseState);
  const [isResponsiveMenuOpen, setIsResponsiveMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const isResponsiveNav = useMediaQueryState(RESPONSIVE_NAV_MEDIA_QUERY);
  const isMobileNav = useMediaQueryState(MOBILE_NAV_MEDIA_QUERY);
  const isSidebarEffectivelyCollapsed = !isResponsiveNav && isSidebarCollapsed;
  const activeNavItem = useMemo(
    () =>
      HOSTED_NAV_ITEMS.find(
        (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
      ) || PRIMARY_NAV[0],
    [location.pathname]
  );
  const accountDisplayName = session?.tenant.name || "Relyo";
  const accountDisplayEmail = session?.user.email || "-";
  const accountTriggerTitle = `${accountDisplayName} · ${accountDisplayEmail}`;
  const accountInitial = (accountDisplayName || accountDisplayEmail).trim().charAt(0).toUpperCase() || "R";

  useEffect(() => {
    if (!isResponsiveNav) {
      setIsResponsiveMenuOpen(false);
    }
  }, [isResponsiveNav]);

  useEffect(() => {
    setIsAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isResponsiveNav || !isResponsiveMenuOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsResponsiveMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isResponsiveMenuOpen, isResponsiveNav]);

  useEffect(() => {
    if (!isMobileNav || !isResponsiveMenuOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileNav, isResponsiveMenuOpen]);

  useEffect(() => {
    if (!isAccountMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsAccountMenuOpen(false);
      }
    };

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      dispatchEasyShellContext({
        activeAppId: activeNavItem.app_id,
        apps: [...primaryNav, ...ACCOUNT_NAV].map((item) => ({
          app_id: item.app_id,
          name: item.label,
          mount_path: item.to,
          capabilities: [],
        })),
        recentEvents: [],
      });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [activeNavItem.app_id, location.pathname, socialCapabilities.data?.enabled]);

  function navigateWithinShell(path: string) {
    navigate(path);
    setIsAccountMenuOpen(false);
    if (isResponsiveNav) {
      setIsResponsiveMenuOpen(false);
    }
  }

  async function handleLogout() {
    await logout();
    setIsAccountMenuOpen(false);
    navigate("/login", { replace: true });
  }

  function toggleSidebarCollapsed() {
    setIsSidebarCollapsed((current) => {
      const nextValue = !current;
      writeStoredCollapseState(nextValue);
      return nextValue;
    });
  }

  function renderAccountMenu(options?: { compact?: boolean; dropdown?: boolean }) {
    const compact = options?.compact ?? false;
    const dropdown = options?.dropdown ?? false;

    return (
      <div
        className={`hosted-account-menu-shell${isAccountMenuOpen ? " is-open" : ""}${compact ? " is-compact" : ""}${
          dropdown ? " is-dropdown" : ""
        }`}
        ref={accountMenuRef}
      >
        {isAccountMenuOpen ? (
          <div
            className={`hosted-account-menu-panel${dropdown ? " is-dropdown" : ""}`}
            role="menu"
            aria-label="Menu de l'espace"
          >
            <div className="hosted-account-menu-header">
              <strong>{accountDisplayName}</strong>
              <span>{accountDisplayEmail}</span>
              <div className="hosted-account-menu-pills">
                <span className="hosted-tenant-pill">{session?.tenant.slug || "-"}</span>
                <span className="hosted-tenant-pill is-muted">{roleLabel(session?.membership.role || "-")}</span>
              </div>
            </div>

            {ACCOUNT_NAV.filter(item => item.app_id !== "admin" || session?.user.is_platform_admin).map((item) => (
              <button
                key={item.app_id}
                type="button"
                role="menuitem"
                className={`hosted-account-menu-item${item.app_id === activeNavItem.app_id ? " is-active" : ""}`}
                onClick={() => navigateWithinShell(item.to)}
              >
                <AppNavIcon appId={item.app_id} />
                <span>{item.label}</span>
              </button>
            ))}

            <button
              type="button"
              role="menuitem"
              className="hosted-account-menu-item is-danger"
              disabled={isSubmitting}
              onClick={() => void handleLogout()}
            >
              <IconLogout2 size="1em" stroke={1.9} />
              <span>Se deconnecter</span>
            </button>
          </div>
        ) : null}

        <button
          type="button"
          className={`hosted-account-trigger${compact ? " is-compact" : ""}${
            !compact && isSidebarEffectivelyCollapsed ? " is-collapsed" : ""
          }`}
          aria-haspopup="menu"
          aria-expanded={isAccountMenuOpen}
          aria-label="Ouvrir le menu du workspace"
          title={accountTriggerTitle}
          onClick={() => setIsAccountMenuOpen((current) => !current)}
        >
          <span className="hosted-account-trigger-avatar" aria-hidden={true}>
            {accountInitial}
          </span>
          {!compact && !isSidebarEffectivelyCollapsed ? (
            <>
              <span className="hosted-account-trigger-copy">
                <strong>{accountDisplayName}</strong>
                <span>{accountDisplayEmail}</span>
              </span>
              <IconChevronDown className="hosted-account-trigger-caret" size="1em" stroke={1.9} />
            </>
          ) : null}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`workspace-shell hosted-workspace-shell${isSidebarEffectivelyCollapsed ? " is-sidebar-collapsed" : ""}${
        isResponsiveNav ? " is-responsive-nav" : ""
      }${isMobileNav ? " is-mobile-nav" : ""}${isResponsiveMenuOpen ? " is-mobile-menu-open" : ""}`}
    >
      <aside className={`workspace-sidebar hosted-workspace-sidebar${isSidebarEffectivelyCollapsed ? " is-collapsed" : ""}`}>
        <div className="sidebar-head">
          <div className="sidebar-head-main">
            <div className="workspace-brand">
              {!isSidebarEffectivelyCollapsed ? (
                <RelioWordmark className="hosted-sidebar-wordmark" />
              ) : (
                <RelioMonogram className="hosted-sidebar-monogram" />
              )}
            </div>
          </div>
          <div className="sidebar-head-actions">
            {isResponsiveNav ? (
              <div className="workspace-active-app" aria-live="polite">
                <AppNavIcon appId={activeNavItem.app_id} />
                <span>{activeNavItem.label}</span>
              </div>
            ) : null}
            {isResponsiveNav ? (
              <button
                type="button"
                className="sidebar-toggle"
                aria-label={isResponsiveMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                aria-expanded={isResponsiveMenuOpen}
                aria-controls="app-nav"
                title={isResponsiveMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                onClick={() => {
                  setIsAccountMenuOpen(false);
                  setIsResponsiveMenuOpen((current) => !current);
                }}
              >
                {isResponsiveMenuOpen ? <IconX size="1em" stroke={1.9} /> : <IconMenu2 size="1em" stroke={1.9} />}
              </button>
            ) : (
              <button
                type="button"
                className="sidebar-toggle"
                aria-label={isSidebarEffectivelyCollapsed ? "Ouvrir le menu" : "Reduire le menu"}
                title={isSidebarEffectivelyCollapsed ? "Ouvrir le menu" : "Reduire le menu"}
                onClick={toggleSidebarCollapsed}
              >
                {isSidebarEffectivelyCollapsed ? (
                  <IconChevronRight size="1em" stroke={1.9} />
                ) : (
                  <IconChevronLeft size="1em" stroke={1.9} />
                )}
              </button>
            )}
            {isResponsiveNav ? renderAccountMenu({ compact: true, dropdown: true }) : null}
          </div>
        </div>

        <div className="hosted-sidebar-body">
          <nav id="app-nav" className="app-nav" aria-label="Applications" hidden={isResponsiveNav && !isResponsiveMenuOpen}>
            {primaryNav.map((item) => (
              <button
                key={item.app_id}
                type="button"
                className={`app-link${item.app_id === activeNavItem.app_id ? " is-active" : ""}`}
                onClick={() => navigateWithinShell(item.to)}
                aria-label={item.label}
                title={item.description}
              >
                <AppNavIcon appId={item.app_id} />
                {!isSidebarEffectivelyCollapsed ? <span className="nav-name">{item.label}</span> : null}
              </button>
            ))}
          </nav>

          <div className="hosted-sidebar-footer">
            {!isResponsiveNav ? renderAccountMenu() : null}
          </div>
        </div>
      </aside>

      {isResponsiveNav && isResponsiveMenuOpen ? (
        <button
          type="button"
          className="workspace-menu-backdrop"
          aria-label="Fermer le menu"
          onClick={() => setIsResponsiveMenuOpen(false)}
        />
      ) : null}

      <main className="workspace-main hosted-main-shell">
        <section className="frame-stack hosted-frame-stack">
          <div className="hosted-shell-page">
            <div className="hosted-shell-page-content">
              <Outlet />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

const hostedRouteErrorElement = <HostedRouteErrorPage />;

const router = createBrowserRouter([
  { path: "/activate", element: <InvitationActivationPage />, errorElement: hostedRouteErrorElement },
  {
    path: "/login",
    element: <AuthCard />,
    errorElement: hostedRouteErrorElement,
  },
  {
    path: "/register",
    element: <Navigate to="/login" replace />,
    errorElement: hostedRouteErrorElement,
  },
  {
    element: <RequireHostedSession />,
    errorElement: hostedRouteErrorElement,
    children: [
      {
        element: <HostedShellLayout />,
        children: [
          {
            path: "/",
            element: <Navigate to="/events" replace />,
          },
          { path: "/admin/companies", element: <PlatformTenantsPage /> },
          { path: "/admin/companies/:tenantPublicId", element: <PlatformTenantPage /> },
          ...createHostedEventPlannerRoutes(),
          { path: "/events/settings", element: <LegacySettingsRedirect to="/settings/events" /> },
          { path: "/newsletter/settings", element: <LegacySettingsRedirect to="/settings/newsletter" /> },
          {
            path: "/settings",
            element: <HostedSettingsLayout />,
            children: [
              { index: true, element: <HostedSettingsPage /> },
              { path: "users", element: <TenantUsersPage /> },
              { path: "newsletter", element: <HostedNewsletterSettingsPage /> },
              { path: "newsletter/tracking", element: <NewsletterTrackingSettingsPage /> },
              { path: "newsletter/subscription-emails", element: <NewsletterEmailTemplatesPage /> },
              { path: "events/:connectorKey?", element: <HostedEventSettingsPage /> },
            ],
          },
          {
            path: "/billing",
            element: <HostedBillingPage />,
          },
          ...createHostedContactsRoutes(),
          ...createHostedMediaRoutes(),
          ...createHostedNewsletterRoutes(),
          ...createHostedSocialPublicationRoutes(),
          {
            path: "*",
            element: (
              <HostedPlaceholderPage
                kicker="Navigation"
                title="Page introuvable"
                description="Cette page n'existe pas dans le workspace Relyo."
              />
            ),
          },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <AppProviders>
      <HostedSessionProvider>
        <RouterProvider router={router} />
      </HostedSessionProvider>
    </AppProviders>
  );
}
